import React, { useState, ChangeEvent } from 'react';
import { Box, Typography, Stack, IconButton, TextField, MenuItem, Button, Divider } from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { SubmissionCategory, TSubmissionCategoryAward } from '../../../../types/global/helper';
import { CustomChipGroup } from '../../../Chip/CustomChipGroup';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Add, Delete } from '@mui/icons-material';
import { theme } from '../../../../constants/theme';
import { DialogueSheet } from '../../../DialogueSheet/DialogueSheet';
import { isEmpty } from '../../../../utils/lodash';
import { CustomizedSwitches } from '../../../Switch/Switch';
// import { FEE_CONFIG } from '../../../../config';
import CustomButton from '../../../Button/CustomButton';
import { ThemeStyle } from '../../../../module/ThemeManager';
import { E_FEATURE_FLAGS, useFeatureFlag } from '../../../../hook/useFeatureFlag';
import { formatCurrency, getPriceForDisplay } from '../../../../constants/currency';
import { submissionPricingSelector } from '../../../../selectors/selectors';
import { useSelector } from 'react-redux';
import { NumericFormat } from 'react-number-format';

const calculateFeeBreakdown = (
    fee: number | string,
    pricingConfig: {
        submissionFlatFee: number;
        addedPercentagePerSubmission: number;
    },
) => {
    const feeInDollars = typeof fee === 'string' ? parseFloat(fee) || 0 : fee;
    const thspianFee = pricingConfig.submissionFlatFee;
    const submissionFee = feeInDollars * (pricingConfig.addedPercentagePerSubmission / 100);
    const creatorEarnings = feeInDollars - thspianFee - submissionFee;

    return {
        totalFee: feeInDollars,
        thspianFee,
        submissionFee,
        creatorEarnings,
    };
};

export type CatField =
    | 'category_name'
    | 'category_description'
    | 'with_fee'
    | 'fee'
    | 'currency'
    | 'with_subscriber_discount'
    | 'subscriber_discount'
    | 'is_free_for_subscriber'
    | 'with_tiered_pricing'
    | 'tier_fee'
    | 'prize_structure';

interface SubmissionCategoryDisplayProps {
    category: SubmissionCategory;
    index: number;
    isEditing: boolean;
    onDelete?: () => void;
    onEdit?: () => void;
    onRemoveCategory?: () => void;
    onDuplicate?: () => void;
    onCategoryChange: (index: number, field: CatField, value: string | boolean | number | any) => void;
    onDeleteCategoryAward?: (index: number, awardId: string) => void;
    onDeletePayout?: (categoryIndex: number, prizeIndex: number, payoutIndex: number, awardId: string) => void;
    hasSubmissions: boolean;
}

export const SubmissionCategoryDisplay: React.FC<SubmissionCategoryDisplayProps> = ({
    category,
    index,
    isEditing,
    onDelete,
    onRemoveCategory,
    onEdit,
    onDuplicate,
    onCategoryChange,
    onDeleteCategoryAward,
    onDeletePayout,
    hasSubmissions,
}) => {
    const { themeStyle } = useThemeContext();
    const [subscriptions] = useFeatureFlag([E_FEATURE_FLAGS.SUBSCRIPTIONS]);
    const submissionPricing = useSelector(submissionPricingSelector);

    const hasErrors = {
        name: category?.category_name?.trim() === '',
        description: category?.category_description?.trim() === '',
        fee:
            category &&
            category.with_fee &&
            (!category.fee || (category.fee as unknown as number) < submissionPricing.minSubmissionPrice / 100),
        currency: category && category.with_fee && !category.currency,
        subscriber_discount:
            category &&
            category.with_subscriber_discount &&
            !category.is_free_for_subscriber &&
            (!category.subscriber_discount || category.subscriber_discount <= 0),
    };

    const hasAnyError = Object.values(hasErrors).some(error => error);

    // Add this helper inside SubmissionCategoryDisplay
    const getDiscountedFee = () => {
        const fee = Number(category.fee) || 0;
        const discount = Number(category.subscriber_discount) || 0;
        const discounted = Math.max(fee - (fee * discount) / 100, 5);
        return discounted;
    };

    // Helper functions for tiered pricing
    const updateTier = (tierIndex: number, field: string, value: any) => {
        const currentTiers = category.tier_fee || [];
        const updatedTiers = [...currentTiers];
        updatedTiers[tierIndex] = { ...updatedTiers[tierIndex], [field]: value };
        onCategoryChange(index, 'tier_fee', updatedTiers);
    };

    const addTier = () => {
        const currentTiers = category.tier_fee || [];
        const newTier = {
            label: '',
            fee: 0,
            currency: 'USD',
            with_subscriber_discount: false,
            subscriber_discount: undefined,
            is_free_for_subscriber: false,
        };
        onCategoryChange(index, 'tier_fee', [...currentTiers, newTier]);
    };

    const removeTier = (tierIndex: number) => {
        const currentTiers = category.tier_fee || [];
        const updatedTiers = currentTiers.filter((_, i) => i !== tierIndex);
        onCategoryChange(index, 'tier_fee', updatedTiers);
    };

    const updatePrizePosition = (prizeIndex: number, field: string, value: any) => {
        const currentPrizeStructure = category.prize_structure || [];
        const updatedPrizeStructure = [...currentPrizeStructure];
        updatedPrizeStructure[prizeIndex] = { ...updatedPrizeStructure[prizeIndex], [field]: value };
        onCategoryChange(index, 'prize_structure', updatedPrizeStructure);
    };

    const addPrizePosition = () => {
        const currentPrizeStructure = category.prize_structure || [];
        const newPrize = { position: '', payouts: [] };
        onCategoryChange(index, 'prize_structure', [...currentPrizeStructure, newPrize]);
    };

    const removePrizePosition = (prizeIndex: number) => {
        const currentPrizeStructure = category.prize_structure || [];
        const updatedPrizeStructure = currentPrizeStructure.filter((_, i) => i !== prizeIndex);
        onCategoryChange(index, 'prize_structure', updatedPrizeStructure);
    };

    const removePayout = (prizeIndex: number, payoutIndex: number, awardId: string) => {
        if (hasSubmissions) return; // Disable deletion if there are submissions

        const currentPrizeStructure = category.prize_structure || [];
        const updatedPrizeStructure = [...currentPrizeStructure];
        // ensure the payouts array is treated as any[] to satisfy the expected union type (string[] | IAwardCategory[])
        const updatedPayouts = [...(updatedPrizeStructure[prizeIndex].payouts || [])] as any[];
        updatedPayouts.splice(payoutIndex, 1);
        updatedPrizeStructure[prizeIndex] = {
            ...updatedPrizeStructure[prizeIndex],
            payouts: updatedPayouts as any,
        };
        onCategoryChange(index, 'prize_structure', updatedPrizeStructure);

        // Call onDeletePayout if provided to handle API call
        if (onDeletePayout && awardId) {
            onDeletePayout(index, prizeIndex, payoutIndex, awardId);
        }
    };

    if (!isEditing) {
        return (
            <Box
                sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: theme[themeStyle].placeholderGray50,
                    borderRadius: 1,
                    backgroundColor: theme[themeStyle].gray_text,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    position: 'relative',
                }}>
                {category.category_name && (
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.25rem',
                        }}>
                        {category.category_name}
                    </Typography>
                )}
                <Typography
                    sx={{
                        color: theme[themeStyle].light_gray_text,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '1rem',
                        flex: 1,
                    }}>
                    {category.category_description}
                </Typography>
                {category.with_tiered_pricing && category.tier_fee && category.tier_fee.length > 0 ? (
                    <Box>
                        <Typography
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                                mb: 1,
                            }}>
                            Pricing Tiers
                        </Typography>
                        {category.tier_fee.map((tier, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    mb: 1,
                                    p: 1.5,
                                    backgroundColor: theme[themeStyle].form_black,
                                    borderRadius: 1,
                                }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '1rem',
                                    }}>
                                    {tier.label}:{' '}
                                    {getPriceForDisplay(tier, formatCurrency(Number(tier.fee), tier.currency || 'USD'))}
                                </Typography>
                                {(tier.is_free_for_subscriber || tier.with_subscriber_discount) && (
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                        }}>
                                        {tier.is_free_for_subscriber
                                            ? 'Free for subscribers'
                                            : tier.with_subscriber_discount && tier.subscriber_discount
                                            ? `${tier.subscriber_discount}% subscriber discount`
                                            : ''}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    category.with_fee &&
                    category &&
                    category.fee &&
                    (category.fee as unknown as number) > 0 && (
                        <Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    mb: 0.5,
                                }}>
                                Submission Fee
                            </Typography>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '1.5rem',
                                }}>
                                {getPriceForDisplay(category, `$${category.fee}`)}
                            </Typography>
                        </Box>
                    )
                )}
                {/* Use CustomChipGroup for awards when not editing */}
                {category.category_awards && !isEmpty(category.category_awards) && (
                    <CustomChipGroup
                        tags={category.category_awards.map(cat =>
                            cat && cat?.category_title ? cat?.category_title : '',
                        )}
                        withWrap
                        withNiceColor
                    />
                )}
                {category.prize_structure && category.prize_structure.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                                mb: 1,
                            }}>
                            Prize Structure
                        </Typography>
                        {category.prize_structure.map((prize, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    mb: 1,
                                    p: 1.5,
                                    backgroundColor: theme[themeStyle].form_black,
                                    borderRadius: 1,
                                }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.875rem',
                                    }}>
                                    {prize.position}
                                </Typography>
                                {prize.payouts && prize.payouts.length > 0 && (
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                        }}>
                                        {prize.payouts.length} payout{prize.payouts.length !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                border: '1px solid',
                borderColor: hasAnyError ? theme[themeStyle].primary_red : theme[themeStyle].placeholderGray50,
                borderRadius: 1,
                backgroundColor: theme[themeStyle].gray_text,
                position: 'relative',
            }}>
            {isEditing && (
                <IconButton
                    onClick={onDelete}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: theme[themeStyle].primary_red,
                        '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            )}
            <Stack spacing={2}>
                <TextField
                    label="Category Name"
                    value={category.category_name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        onCategoryChange(index, 'category_name', e.target.value);
                    }}
                    disabled={!isEditing}
                    error={hasErrors.name}
                    helperText={hasErrors.name ? 'Category name is required' : ''}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme[themeStyle].gray_text,
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '& fieldset': {
                                borderColor: hasErrors.name
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].placeholderGray50,
                            },
                            '&:hover fieldset': {
                                borderColor: hasErrors.name
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].placeholderGray50,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: hasErrors.name
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].highlight,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'transparent',
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&.Mui-disabled': {
                                color: theme[themeStyle].light_gray_text,
                            },
                            '&.Mui-error': {
                                color: theme[themeStyle].primary_red,
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&.Mui-disabled': {
                                color: theme[themeStyle].tag_white,
                                WebkitTextFillColor: theme[themeStyle].tag_white,
                            },
                        },
                    }}
                />
                <TextField
                    label="Category Description"
                    value={category.category_description}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        onCategoryChange(index, 'category_description', e.target.value);
                    }}
                    disabled={!isEditing}
                    multiline
                    rows={2}
                    error={hasErrors.description}
                    helperText={hasErrors.description ? 'Category description is required' : ''}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: theme[themeStyle].gray_text,
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '& fieldset': {
                                borderColor: hasErrors.description
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].placeholderGray50,
                            },
                            '&:hover fieldset': {
                                borderColor: hasErrors.description
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].placeholderGray50,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: hasErrors.description
                                    ? theme[themeStyle].primary_red
                                    : theme[themeStyle].highlight,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'transparent',
                                '& fieldset': {
                                    borderColor: 'transparent',
                                },
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme[themeStyle].light_gray_text,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&.Mui-disabled': {
                                color: theme[themeStyle].light_gray_text,
                            },
                            '&.Mui-error': {
                                color: theme[themeStyle].primary_red,
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&.Mui-disabled': {
                                color: theme[themeStyle].tag_white,
                                WebkitTextFillColor: theme[themeStyle].tag_white,
                            },
                        },
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CustomizedSwitches
                        label="With Fee"
                        switchProps={{
                            checked: category.with_fee ?? false,
                            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                onCategoryChange(index, 'with_fee', e.target.checked);
                                if (e.target.checked) {
                                    // Disable tiered pricing when regular fee is enabled
                                    onCategoryChange(index, 'with_tiered_pricing', false);
                                }
                            },
                            disabled: !isEditing || (category.with_tiered_pricing ?? false),
                        }}
                    />
                    {category.with_fee && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    type="number"
                                    label="Fee Amount"
                                    value={category.fee}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const value = e.target.value;
                                        onCategoryChange(index, 'fee', value);
                                    }}
                                    disabled={!isEditing}
                                    error={hasErrors.fee}
                                    helperText={
                                        hasErrors.fee
                                            ? `Fee amount must be greater than ${formatCurrency(
                                                  submissionPricing.minSubmissionPrice / 100,
                                              )}`
                                            : ''
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <Typography sx={{ color: theme[themeStyle].tag_white, mr: 1 }}>
                                                $
                                            </Typography>
                                        ),
                                        inputProps: {
                                            min: 0,
                                            step: 0.01,
                                        },
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].highlight,
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'transparent',
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                },
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].light_gray_text,
                                            },
                                            '&.Mui-error': {
                                                color: theme[themeStyle].primary_red,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].tag_white,
                                                WebkitTextFillColor: theme[themeStyle].tag_white,
                                            },
                                        },
                                    }}
                                />
                                <TextField
                                    select
                                    label="Currency"
                                    value={category.currency}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        onCategoryChange(index, 'currency', e.target.value);
                                        console.log(e.target.value);
                                    }}
                                    disabled={!isEditing}
                                    error={hasErrors.currency}
                                    helperText={hasErrors.currency ? 'Currency is required' : ''}
                                    placeholder="Select a currency"
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: hasErrors.currency
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: hasErrors.currency
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: hasErrors.currency
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].highlight,
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'transparent',
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                },
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].light_gray_text,
                                            },
                                            '&.Mui-error': {
                                                color: theme[themeStyle].primary_red,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].tag_white,
                                                WebkitTextFillColor: theme[themeStyle].tag_white,
                                            },
                                        },
                                    }}>
                                    <MenuItem value="USD">USD</MenuItem>
                                </TextField>
                            </Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic',
                                }}>
                                Note: While you will receive your earnings in USD, participants will be charged in their
                                local currency. Please account for exchange rates and bank transfer fees when setting
                                your fee amount.
                            </Typography>
                            {category && category.fee && (category.fee as unknown as number) > 0 && (
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: theme[themeStyle].form_black,
                                        borderRadius: 1,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                            mb: 1,
                                        }}>
                                        Fee Breakdown:
                                    </Typography>
                                    {(() => {
                                        const breakdown = calculateFeeBreakdown(category.fee, {
                                            submissionFlatFee: submissionPricing.submissionFlatFee / 100,
                                            addedPercentagePerSubmission:
                                                submissionPricing.addedPercentagePerSubmission,
                                        });
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.5,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    Total Fee: ${breakdown.totalFee.toFixed(2)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    Thspian Fee: ${breakdown.thspianFee.toFixed(2)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    Submission Fee: ${breakdown.submissionFee.toFixed(2)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].highlight,
                                                        fontSize: '0.875rem',
                                                        fontWeight: 'bold',
                                                    }}>
                                                    Your Earnings: ${breakdown.creatorEarnings.toFixed(2)}
                                                </Typography>
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {category.with_fee && (
                        <Stack direction={'row'} alignItems={'center'} gap={2}>
                            <CustomizedSwitches
                                label="Special Subscriber Discounts"
                                switchProps={{
                                    checked: category.with_subscriber_discount ?? false,
                                    onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                        onCategoryChange(index, 'with_subscriber_discount', e.target.checked),
                                    disabled: !subscriptions || !isEditing,
                                }}
                            />

                            <CustomizedSwitches
                                label="Free For Subscribers"
                                switchProps={{
                                    checked: category.is_free_for_subscriber ?? false,
                                    onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                        onCategoryChange(index, 'is_free_for_subscriber', e.target.checked),
                                    disabled: !subscriptions || !isEditing,
                                }}
                            />
                        </Stack>
                    )}
                    {category.with_subscriber_discount && !category.is_free_for_subscriber && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    type="number"
                                    label="Discount Percentage"
                                    value={category.subscriber_discount}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        const value = Number(e.target.value);
                                        const fee = Number(category.fee) || 0;
                                        // Prevent discount from reducing fee below $5
                                        const minDiscount = fee > 0 ? Math.floor(((fee - 5) / fee) * 100) : 0;
                                        if (value > 0 && fee - (fee * value) / 100 < 5) {
                                            onCategoryChange(index, 'subscriber_discount', minDiscount);
                                        } else {
                                            onCategoryChange(index, 'subscriber_discount', value);
                                        }
                                    }}
                                    disabled={!isEditing}
                                    error={hasErrors.subscriber_discount}
                                    helperText={
                                        hasErrors.subscriber_discount
                                            ? 'Discount must be greater than 0'
                                            : category.fee &&
                                              category.subscriber_discount &&
                                              Number(category.fee) -
                                                  (Number(category.fee) * Number(category.subscriber_discount)) / 100 <
                                                  5
                                            ? 'Discounted fee cannot be less than $5'
                                            : ''
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <Typography sx={{ color: theme[themeStyle].tag_white, mr: 1 }}>
                                                %
                                            </Typography>
                                        ),
                                        inputProps: {
                                            min: 0,
                                            step: 0.01,
                                        },
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme[themeStyle].gray_text,
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '& fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].placeholderGray50,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: hasErrors.fee
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].highlight,
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'transparent',
                                                '& fieldset': {
                                                    borderColor: 'transparent',
                                                },
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].light_gray_text,
                                            },
                                            '&.Mui-error': {
                                                color: theme[themeStyle].primary_red,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&.Mui-disabled': {
                                                color: theme[themeStyle].tag_white,
                                                WebkitTextFillColor: theme[themeStyle].tag_white,
                                            },
                                        },
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].light_gray_text,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic',
                                }}>
                                This discount will be applied to users who subscribe to the platform and will increase
                                visibility to your campaign.
                            </Typography>
                            {category && category.subscriber_discount && category.subscriber_discount > 0 && (
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: theme[themeStyle].form_black,
                                        borderRadius: 1,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].light_gray_text,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.875rem',
                                            mb: 1,
                                        }}>
                                        Fee Breakdown:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography sx={{ color: theme[themeStyle].tag_white, fontSize: '0.875rem' }}>
                                            Original Fee: ${Number(category.fee).toFixed(2)}
                                        </Typography>
                                        <Typography sx={{ color: theme[themeStyle].tag_white, fontSize: '0.875rem' }}>
                                            Discount: {category.subscriber_discount}%
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].highlight,
                                                fontSize: '0.875rem',
                                                fontWeight: 'bold',
                                            }}>
                                            Discounted Fee: ${getDiscountedFee().toFixed(2)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontSize: '0.875rem',
                                                textDecoration: 'line-through',
                                            }}>
                                            Thspian Fee: ${submissionPricing.submissionFlatFee.toFixed(2)} (waived for
                                            subscribers)
                                        </Typography>
                                        <Typography sx={{ color: theme[themeStyle].tag_white, fontSize: '0.875rem' }}>
                                            Submission Fee: $
                                            {(
                                                getDiscountedFee() * submissionPricing.addedPercentagePerSubmission
                                            ).toFixed(2)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].highlight,
                                                fontSize: '0.875rem',
                                                fontWeight: 'bold',
                                            }}>
                                            Your Earnings: $
                                            {(
                                                getDiscountedFee() -
                                                getDiscountedFee() * submissionPricing.addedPercentagePerSubmission
                                            ).toFixed(2)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                <Divider sx={{ my: 2, borderColor: theme[themeStyle].placeholderGray50 }} />
                <CustomizedSwitches
                    label="Tiered Pricing"
                    switchProps={{
                        checked: category.with_tiered_pricing === true,
                        onChange: (e: ChangeEvent<HTMLInputElement>) => {
                            // Just update the toggle - handleCategoryChange will handle related fields
                            onCategoryChange(index, 'with_tiered_pricing', e.target.checked);
                        },
                        disabled: !isEditing,
                    }}
                />
                {category.with_tiered_pricing && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Pricing Tiers
                            </Typography>
                            <Button
                                startIcon={<Add />}
                                onClick={addTier}
                                size="small"
                                disabled={!isEditing}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_orange,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].background_gradient.end,
                                    },
                                }}>
                                Add Tier
                            </Button>
                        </Box>
                        {(category.tier_fee || []).map((tier, tierIndex) => (
                            <Box
                                key={tierIndex}
                                sx={{
                                    background: theme[themeStyle].form_black,
                                    borderRadius: 2,
                                    p: 2,
                                    mb: 2,
                                }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                                    <TextField
                                        label="Tier Label"
                                        value={tier.label}
                                        onChange={e => updateTier(tierIndex, 'label', e.target.value)}
                                        disabled={!isEditing}
                                        fullWidth
                                        placeholder="e.g., Free, Standard, Premium"
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                            },
                                        }}
                                    />
                                    <NumericFormat
                                        customInput={TextField}
                                        label="Fee"
                                        type="text"
                                        value={tier.fee || ''}
                                        onChange={e =>
                                            updateTier(tierIndex, 'fee', Number(e.target.value.replace(/[^0-9.]/g, '')))
                                        }
                                        disabled={!isEditing}
                                        thousandSeparator
                                        prefix="$"
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme[themeStyle].gray_text,
                                                color: theme[themeStyle].tag_white,
                                                '& fieldset': {
                                                    borderColor: theme[themeStyle].placeholderGray50,
                                                },
                                            },
                                        }}
                                    />
                                    {isEditing && (
                                        <IconButton
                                            onClick={() => removeTier(tierIndex)}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                mt: 1,
                                            }}>
                                            <Delete />
                                        </IconButton>
                                    )}
                                </Box>
                                {Number(tier.fee) >= submissionPricing.minSubmissionPrice / 100 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].light_gray_text,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: 13,
                                            }}>
                                            {(() => {
                                                const fee = Number(tier.fee);
                                                const thspianFlat = submissionPricing.submissionFlatFee / 100;
                                                const thspianPercent =
                                                    submissionPricing.addedPercentagePerSubmission / 100;
                                                const thspianPercentFee = fee * thspianPercent;
                                                const thspianTotal = thspianFlat + thspianPercentFee;
                                                const userReceives = Math.max(0, fee - thspianTotal);
                                                return (
                                                    <>
                                                        Thspian fee: ${thspianTotal.toFixed(2)} ($2 + 7%)
                                                        <br />
                                                        You receive: ${userReceives.toFixed(2)}
                                                    </>
                                                );
                                            })()}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        mt: 2,
                                        pt: 2,
                                        borderTop: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                    }}>
                                    <CustomizedSwitches
                                        label="Free for Subscribers"
                                        switchProps={{
                                            checked: tier.is_free_for_subscriber || false,
                                            onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                                updateTier(tierIndex, 'is_free_for_subscriber', e.target.checked),
                                            disabled: !subscriptions || !isEditing,
                                        }}
                                    />
                                    {!tier.is_free_for_subscriber && (
                                        <>
                                            <CustomizedSwitches
                                                label="Subscriber Discount"
                                                switchProps={{
                                                    checked: tier.with_subscriber_discount || false,
                                                    onChange: (e: ChangeEvent<HTMLInputElement>) =>
                                                        updateTier(
                                                            tierIndex,
                                                            'with_subscriber_discount',
                                                            e.target.checked,
                                                        ),
                                                    disabled: !subscriptions || !isEditing,
                                                }}
                                            />
                                            {tier.with_subscriber_discount && (
                                                <NumericFormat
                                                    customInput={TextField}
                                                    label="Discount Percentage"
                                                    type="text"
                                                    value={tier.subscriber_discount || ''}
                                                    onChange={e =>
                                                        updateTier(
                                                            tierIndex,
                                                            'subscriber_discount',
                                                            Number(e.target.value.replace(/[^0-9.]/g, '')),
                                                        )
                                                    }
                                                    disabled={!subscriptions || !isEditing}
                                                    suffix="%"
                                                    fullWidth
                                                    sx={{
                                                        mt: 1,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: theme[themeStyle].gray_text,
                                                            color: theme[themeStyle].tag_white,
                                                            '& fieldset': {
                                                                borderColor: theme[themeStyle].placeholderGray50,
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        ))}
                        {(category.tier_fee || []).length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        Prize Structure
                                    </Typography>
                                    {isEditing && (
                                        <Button
                                            startIcon={<Add />}
                                            onClick={addPrizePosition}
                                            size="small"
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                color: theme[themeStyle].primary_orange,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].background_gradient.end,
                                                },
                                            }}>
                                            Add Position
                                        </Button>
                                    )}
                                </Box>
                                {(category.prize_structure || []).map((prize, prizeIndex) => (
                                    <Box
                                        key={prizeIndex}
                                        sx={{
                                            background: theme[themeStyle].form_black,
                                            borderRadius: 2,
                                            p: 2,
                                            mb: 2,
                                        }}>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                            <TextField
                                                label="Position"
                                                value={prize.position}
                                                onChange={e =>
                                                    updatePrizePosition(prizeIndex, 'position', e.target.value)
                                                }
                                                disabled={!isEditing}
                                                placeholder="e.g., 1st Place, 2nd Place"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: theme[themeStyle].gray_text,
                                                        color: theme[themeStyle].tag_white,
                                                        '& fieldset': {
                                                            borderColor: theme[themeStyle].placeholderGray50,
                                                        },
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: theme[themeStyle].tag_white,
                                                    },
                                                }}
                                            />
                                            {isEditing && (
                                                <IconButton
                                                    onClick={() => removePrizePosition(prizeIndex)}
                                                    sx={{
                                                        color: theme[themeStyle].primary_orange,
                                                        mt: 1,
                                                    }}>
                                                    <Delete />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].light_gray_text,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                mb: 1,
                                            }}>
                                            Payouts: {prize.payouts?.length || 0} award
                                            {prize.payouts?.length !== 1 ? 's' : ''}
                                        </Typography>
                                        {prize.payouts && prize.payouts.length > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 1.5,
                                                    pt: 1.5,
                                                    borderTop: `1px solid ${theme[themeStyle].placeholderGray50}`,
                                                }}>
                                                {prize.payouts.map((payout: any, payoutIndex: number) => {
                                                    // Handle both cases: payout as ID string or populated object
                                                    const awardId = typeof payout === 'string' ? payout : payout._id;
                                                    const award = typeof payout === 'object' && payout ? payout : null;

                                                    // Try to find award from category_awards if payout is just an ID
                                                    const awardFromCategory =
                                                        !award &&
                                                        category.category_awards?.find((a: any) => a._id === awardId);

                                                    const displayAward = award || awardFromCategory;

                                                    console.log(displayAward);

                                                    if (!displayAward) {
                                                        // If we can't find the award, just show the ID
                                                        return (
                                                            <Typography
                                                                key={payoutIndex}
                                                                sx={{
                                                                    color: theme[themeStyle].light_gray_text,
                                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                    fontSize: '0.75rem',
                                                                    mb: 0.5,
                                                                }}>
                                                                Award ID: {awardId}
                                                            </Typography>
                                                        );
                                                    }

                                                    return (
                                                        <Box
                                                            key={payoutIndex}
                                                            sx={{
                                                                mb: 1,
                                                                p: 1,
                                                                backgroundColor: theme[themeStyle].gray_text,
                                                                borderRadius: 1,
                                                                position: 'relative',
                                                            }}>
                                                            {isEditing && (
                                                                <IconButton
                                                                    onClick={() =>
                                                                        removePayout(prizeIndex, payoutIndex, awardId)
                                                                    }
                                                                    disabled={hasSubmissions}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 4,
                                                                        right: 4,
                                                                        color: hasSubmissions
                                                                            ? theme[themeStyle].light_gray_text
                                                                            : theme[themeStyle].primary_white,
                                                                        '&:hover': {
                                                                            color: hasSubmissions
                                                                                ? theme[themeStyle].light_gray_text
                                                                                : theme[themeStyle].primary_orange,
                                                                        },
                                                                        '&.Mui-disabled': {
                                                                            opacity: 0.5,
                                                                        },
                                                                    }}
                                                                    size="small">
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].tag_white,
                                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    fontSize: '0.875rem',
                                                                    mb: 0.5,
                                                                    pr: isEditing ? 4 : 0,
                                                                }}>
                                                                {displayAward.tier_name || 'Award'}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    color: theme[themeStyle].light_gray_text,
                                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                    fontSize: '0.75rem',
                                                                }}>
                                                                Prize:{' '}
                                                                {formatCurrency(
                                                                    Number(displayAward.prize) || 0,
                                                                    displayAward.currency || 'USD',
                                                                )}
                                                            </Typography>
                                                            {displayAward.additional_prize &&
                                                                Array.isArray(displayAward.additional_prize) &&
                                                                displayAward.additional_prize.length > 0 &&
                                                                displayAward.additional_prize.some(
                                                                    (p: string) => p && p.trim(),
                                                                ) && (
                                                                    <Typography
                                                                        sx={{
                                                                            color: theme[themeStyle].light_gray_text,
                                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                                            fontSize: '0.75rem',
                                                                            mt: 0.5,
                                                                        }}>
                                                                        Additional:{' '}
                                                                        {displayAward.additional_prize
                                                                            .filter((p: string) => p && p.trim())
                                                                            .join(', ')}
                                                                    </Typography>
                                                                )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                                {(!category.prize_structure || category.prize_structure.length === 0) && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: theme[themeStyle].form_black,
                                            borderRadius: 1,
                                            border: `1px dashed ${theme[themeStyle].placeholderGray50}`,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].light_gray_text,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                            }}>
                                            {isEditing
                                                ? 'Click "Add Position" to create a new prize position'
                                                : 'No prize positions defined'}
                                        </Typography>
                                    </Box>
                                )}
                                {category.prize_structure && category.prize_structure.length > 0 && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: theme[themeStyle].form_black,
                                            borderRadius: 1,
                                            border: `1px dashed ${theme[themeStyle].placeholderGray50}`,
                                            mt: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].light_gray_text,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                            }}>
                                            You can add new awards or prizes for these positions from the Award tab
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Stack>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                {onDuplicate && (
                    <CustomButton
                        label="Duplicate Category"
                        onClick={onDuplicate}
                        customStyle={{ flex: 1, marginTop: 0 }}
                    />
                )}
                <CustomButton
                    label="Delete Category"
                    onClick={onRemoveCategory}
                    customStyle={{ flex: onDuplicate ? 1 : '100%', marginTop: 0 }}
                />
            </Box>
            {category.category_awards && category.category_awards.length > 0 && (
                <CategoryAwardsCollapsible
                    awards={category.category_awards}
                    onDeleteAward={awardId => {
                        if (onDeleteCategoryAward) onDeleteCategoryAward(index, awardId);
                    }}
                    hasSubmissions={hasSubmissions}
                    themeStyle={themeStyle}
                />
            )}
        </Box>
    );
};

// Add this new component before SubmissionCategoryDisplay
interface CategoryAwardsCollapsibleProps {
    awards: TSubmissionCategoryAward[];
    onDeleteAward: (awardId: string) => void;
    hasSubmissions: boolean;
    themeStyle: ThemeStyle;
}

const CategoryAwardsCollapsible: React.FC<CategoryAwardsCollapsibleProps> = ({
    awards,
    onDeleteAward,
    hasSubmissions,
    themeStyle,
}) => {
    const [expandedAwards, setExpandedAwards] = useState<Record<string, boolean>>({});
    const [dialogueOpen, setDialogueOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const toggleAward = (awardId: string) => {
        setExpandedAwards(prev => ({
            ...prev,
            [awardId]: !prev[awardId],
        }));
    };

    const handleDeleteClick = (awardId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setPendingDeleteId(awardId);
        setDialogueOpen(true);
    };

    const handleAgree = () => {
        if (pendingDeleteId) {
            onDeleteAward(pendingDeleteId);
        }
        setDialogueOpen(false);
        setPendingDeleteId(null);
    };

    const handleCancel = () => {
        setDialogueOpen(false);
        setPendingDeleteId(null);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '1rem',
                    mb: 1,
                }}>
                Category Awards
            </Typography>
            {awards.map((award, index) => (
                <Box
                    key={award._id}
                    sx={{
                        border: '1px solid',
                        borderColor: theme[themeStyle].placeholderGray50,
                        borderRadius: 1,
                        mb: 1,
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: theme[themeStyle].gray_text,
                            cursor: 'pointer',
                        }}
                        onClick={() => toggleAward(award._id)}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {award.category_title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hasSubmissions ? (
                                <Tooltip title="Awards cannot be deleted when a submission has been made to the contest.">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled
                                            sx={{
                                                color: theme[themeStyle].placeholderGray,
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                            }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <IconButton
                                    size="small"
                                    onClick={e => handleDeleteClick(award._id, e)}
                                    sx={{
                                        color: theme[themeStyle].primary_red,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                        },
                                    }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    transform: expandedAwards[award._id] ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s',
                                }}>
                                <ArrowDropDownIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    {expandedAwards[award._id] && (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: theme[themeStyle].form_black,
                            }}>
                            <Box component="ol" sx={{ m: 0, pl: 2 }}>
                                {!!award.prize && Number(award.prize) > 0 && (
                                    <Box component="li" sx={{ mb: 1 }}>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Prize: ${award.formatted_for_display ?? award.prize}
                                        </Typography>
                                    </Box>
                                )}
                                {award.additional_prize && award.additional_prize.length > 0 && (
                                    <Box component="li">
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                mb: 0.5,
                                            }}>
                                            Additional Prizes:
                                        </Typography>
                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                            {award.additional_prize.map((prize, idx) => (
                                                <Box component="li" key={idx}>
                                                    <Typography
                                                        sx={{
                                                            color: theme[themeStyle].light_gray_text,
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        }}>
                                                        {prize}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {award.sub_category && (
                                    <Box component="li">
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Sub-category: {award.sub_category}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            ))}
            <DialogueSheet
                open={dialogueOpen}
                onClose={handleCancel}
                header="Delete Award"
                body="This award will be deleted from the submission category. Are you sure you want to proceed?"
                negativeText="Cancel"
                positiveText="Delete"
                onAgreeClicked={handleAgree}
                onDisagreeClick={handleCancel}
            />
        </Box>
    );
};
